import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { CalendarHeart, Clock, Flame } from 'lucide-react';
import { Card, CardTitle, CardDescription, Skeleton } from '@/components/ui';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import type { SeasonCategory, SeasonEvent } from '@keywordgod/shared';

const CATEGORY_ORDER: Array<{ key: SeasonCategory; label: string }> = [
  { key: 'holiday', label: '공휴일·명절' },
  { key: 'monthly14', label: '14일 기념일' },
  { key: 'tax', label: '세금' },
  { key: 'work', label: '직장인' },
  { key: 'season', label: '계절' },
  { key: 'school', label: '학사·학교' },
  { key: 'shopping', label: '쇼핑 이벤트' },
];

export function Season() {
  const [filter, setFilter] = useState<SeasonCategory | 'all'>('all');
  const allQ = useQuery({
    queryKey: ['season', 'all'],
    queryFn: () => api.season.all(),
  });
  const nowQ = useQuery({
    queryKey: ['season', 'now', 21],
    queryFn: () => api.season.now(21),
  });

  const events = allQ.data ?? [];
  const filtered = filter === 'all' ? events : events.filter((e) => e.category === filter);

  // 카테고리별 그룹
  const grouped = new Map<string, SeasonEvent[]>();
  for (const e of filtered) {
    if (!grouped.has(e.category)) grouped.set(e.category, []);
    grouped.get(e.category)!.push(e);
  }

  return (
    <div className="space-y-8">
      <section>
        <h1 className="flex items-center gap-3 text-3xl font-extrabold text-white">
          <CalendarHeart className="h-8 w-8 text-gold" />
          시즌 키워드
        </h1>
        <p className="mt-2 text-gray-400">
          공휴일, 기념일, 세금·계절·학사 등 시즌별 추천 키워드. 1,000만 키워드 수집 완료 후에는
          실제 분석 데이터와 자동 연동됩니다.
        </p>
      </section>

      {/* 이번주/다가오는 시즌 — 상단 강조 */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <Flame className="h-5 w-5 text-orange-400" />
          <h2 className="text-xl font-bold text-white">이번주 + 다가오는 시즌 (21일 내)</h2>
        </div>
        {nowQ.isLoading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-32 w-full" />
            ))}
          </div>
        )}
        {nowQ.data && nowQ.data.length === 0 && (
          <Card className="text-center text-gray-500">21일 내 다가오는 시즌이 없습니다.</Card>
        )}
        {nowQ.data && nowQ.data.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {nowQ.data.map((e) => (
              <SeasonCard key={e.id} ev={e} highlight />
            ))}
          </div>
        )}
      </section>

      {/* 카테고리 필터 */}
      <section>
        <div className="flex items-center flex-wrap gap-2 mb-4">
          <FilterChip
            active={filter === 'all'}
            onClick={() => setFilter('all')}
            label={`전체 (${events.length})`}
          />
          {CATEGORY_ORDER.map((c) => {
            const n = events.filter((e) => e.category === c.key).length;
            if (n === 0) return null;
            return (
              <FilterChip
                key={c.key}
                active={filter === c.key}
                onClick={() => setFilter(c.key)}
                label={`${c.label} (${n})`}
              />
            );
          })}
        </div>

        {allQ.isLoading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-28 w-full" />
            ))}
          </div>
        )}

        {/* 그룹별 표시 */}
        {filter === 'all' ? (
          <div className="space-y-8">
            {CATEGORY_ORDER.map((c) => {
              const list = grouped.get(c.key);
              if (!list || list.length === 0) return null;
              return (
                <div key={c.key}>
                  <h3 className="text-sm font-bold text-gold mb-3 uppercase tracking-widest">
                    {c.label}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {list.map((e) => (
                      <SeasonCard key={e.id} ev={e} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {filtered.map((e) => (
              <SeasonCard key={e.id} ev={e} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

// ─── 컴포넌트 ────────────────────────────────────────────────

function SeasonCard({ ev, highlight }: { ev: SeasonEvent; highlight?: boolean }) {
  const visible = ev.keywords.slice(0, 5);
  const more = Math.max(0, ev.keywords.length - 5);

  return (
    <Card
      className={cn(
        'transition-colors',
        highlight && ev.state === 'active' && 'border-emerald-400/50 bg-emerald-400/5',
        highlight && ev.state === 'upcoming' && 'border-gold/50 bg-gold/5',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-xl shrink-0">{ev.emoji ?? '📅'}</span>
          <div className="min-w-0">
            <div className="font-bold text-white truncate">{ev.name}</div>
            <div className="text-[10px] uppercase tracking-widest text-gray-500">
              {ev.categoryLabel}
            </div>
          </div>
        </div>
        <StateBadge ev={ev} />
      </div>

      {ev.description && (
        <div className="mt-2 text-xs text-gray-400 line-clamp-2">{ev.description}</div>
      )}

      <div className="mt-3 flex flex-wrap gap-1.5">
        {visible.map((k) => (
          <Link
            key={k}
            to={`/analyze/${encodeURIComponent(k)}`}
            className="rounded-full bg-gray-800 hover:bg-gold/15 hover:text-gold px-2.5 py-1 text-[11px] text-gray-300 transition-colors"
          >
            {k}
          </Link>
        ))}
        {more > 0 && (
          <span className="rounded-full bg-gray-900 px-2.5 py-1 text-[11px] text-gray-500">
            +{more}
          </span>
        )}
      </div>
    </Card>
  );
}

function StateBadge({ ev }: { ev: SeasonEvent }) {
  if (ev.state === 'active') {
    return (
      <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-emerald-400/15 px-2 py-0.5 text-[10px] font-bold text-emerald-300">
        <span className="relative inline-flex h-1.5 w-1.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400/70" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
        </span>
        진행 중
      </span>
    );
  }
  if (ev.state === 'upcoming') {
    const d = ev.daysUntilStart ?? 0;
    return (
      <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-gold/15 px-2 py-0.5 text-[10px] font-bold text-gold">
        <Clock className="h-3 w-3" />
        D-{d}
      </span>
    );
  }
  return (
    <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-gray-800 px-2 py-0.5 text-[10px] font-bold text-gray-500">
      종료
    </span>
  );
}

function FilterChip({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-full px-3 py-1.5 text-xs font-semibold transition-colors',
        active
          ? 'bg-gold text-black'
          : 'bg-gray-900 text-gray-300 hover:bg-gray-800 border border-gray-800',
      )}
    >
      {label}
    </button>
  );
}
